import { Component, computed, effect, inject, OnInit, signal, untracked } from '@angular/core';
import { Router, NavigationEnd, ActivatedRoute, EventType } from '@angular/router';
import CookieManager from './utils/cookie-manager';
import { HeaderMode, HeaderModeService } from '@core';
import { filter, map } from 'rxjs/operators';
import { toSignal } from '@angular/core/rxjs-interop';
import { setResellerData } from './utils/reseller-data';
import { ProductsService } from './products/products.service';
import { CookieSettingsService } from './core/cookie-settings/cookie-settings.service';

declare let gtag: (...args: (string | number)[]) => void;

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrl: './app.component.scss',
    standalone: false,
})
export class AppComponent implements OnInit {
    private headerModeService = inject(HeaderModeService);
    private router = inject(Router);
    // cookieService is injected here but not used. This
    // is just to get an instance up as early as possible
    private cookieService = inject(CookieSettingsService);
    private product = inject(ProductsService).product;
    private productHeaderMode = computed(() => this.product()?.orderConfig.headerMode);
    protected isMainMenuOpen = signal(false);

    private cookies = new CookieManager('cookiesAllowed');

    private queryParams = toSignal(inject(ActivatedRoute).queryParams);
    private route = toSignal(
        this.router.events.pipe(
            filter((e) => e instanceof NavigationEnd),
            map((e) => e as NavigationEnd),
        ),
    );

    public constructor() {
        // get header from product
        effect(() => {
            const headerMode = this.productHeaderMode();
            untracked(() => {
                if (this.route() && headerMode) {
                    this.headerModeService.setHeaderMode(
                        this.route().urlAfterRedirects.indexOf('/produkte/') === -1 ? 'default' : headerMode,
                        'product',
                    );
                }
            });
        });

        // get header mode from queries
        effect(() => {
            const headerMode = this.queryParams()['hm'] as HeaderMode;

            if (headerMode) {
                this.headerModeService.setHeaderMode(headerMode, 'queryParam');
            }
        });

        // get reseller data from queries
        effect(() => {
            const resellerId = this.queryParams()['r'];
            const additionalResellerData = this.queryParams()['ard'];

            if (resellerId || additionalResellerData) {
                setResellerData({ resellerId, additionalResellerData });
            }
        });
    }

    public ngOnInit(): void {
        this.router.events.subscribe((e) => {
            if (
                e.type === EventType.NavigationEnd &&
                typeof gtag !== 'undefined' &&
                this.cookies.getAllowance('google')
            ) {
                gtag('event', 'page_view');
            }
            if (e.type === EventType.Scroll) {
                const ProductLandingPageRegExp = new RegExp('/produkte/[0-9a-z-_]+$');
                const urlAfterRedirects =
                    e.routerEvent.type === EventType.NavigationEnd ? e.routerEvent.urlAfterRedirects : '';
                if (ProductLandingPageRegExp.test(urlAfterRedirects) && e.position) {
                    // scroll event fires too early on product landing pages,
                    // which results in a bad scroll position when returning
                    // to landing pages via browser back navigation. Scrolling
                    // after a short timeout is a workaround
                    // TODO (christian@ctrl.alt.coop) find a better, more generic solution
                    // @see https://gitlab.alt.coop/astra/versicherung/-/issues/246
                    const [x, y] = e.position;
                    setTimeout(() => window.scrollTo(x, y), 20);
                }
            }
        });
    }

    protected handleMainMenuToggle(isOpen: boolean): void {
        this.isMainMenuOpen.set(isOpen);
    }

    public up(): void {
        window.scroll({ behavior: 'smooth', top: 0 });
    }

    public focusOnMain(): void {
        const main = document.getElementById('main') as HTMLElement;
        if (main) {
            main.setAttribute('tabindex', '-1');
            main.focus();
        }
    }
}
